import {
  CircularProgress,
  Typography,
  DialogActions,
  makeStyles,
} from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { SyntheticEvent, useState, useEffect, useContext } from 'react';
import { Field, Form } from 'react-final-form';
import { Button, FormGroup, ModalV2, Text } from '.';
import mentorsAPI, { MentorId } from '../../api/mentors';
import venturesAPI from '../../api/ventures';
import { VentureId } from '../../api/ventures/types/Venture';
import { useResourceBundles } from '../../contexts/resource-bundles-context';
import { UserContext } from '../../contexts/user-context';
import { lengthField } from '../../utils/form';
import { CLASS_TRACKING } from '../../utils/tracking_class';
import { TestId } from '../Testing/TestId';
import { FormRating, TextFieldWysiwyg, FormSelect } from '../forms/wrappers';

interface FormValues {
  rating: string;
  comment: string;
  ventureId?: string;
}

type Errors = {
  [K in keyof FormValues]?: string;
};

const validateForm = (values: FormValues, hasVentureId: boolean) => {
  const errors: Errors = {};
  if (!values.comment) {
    errors.comment = 'Required';
  }
  if (!values.rating) {
    errors.rating = 'Required';
  }
  if (!hasVentureId && !values.ventureId) {
    errors.ventureId = 'Required';
  }
  return errors;
};

const initialValues = { comment: '' };

interface WriteReviewProps {
  ventureId?: VentureId;
  mentorId: MentorId;
  mentorName?: string;
}

interface WriteReviewModalProps extends WriteReviewProps {
  open: boolean;
  onClose: () => void;
}

interface WriteReviewFormProps extends WriteReviewProps {
  onClose: () => void;
  ventures?: Array<{ value: string; label: string }>;
}

const useFormStyles = makeStyles({
  container: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  contentButton: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
    columnGap: 15,
    marginTop: 20,
  },
  subtitle: {
    margin: '20px 0 10px',
  },
  warningText: {
    width: '100%',
    marginBottom: '16px',
    display: 'block',
  },
  warningActions: {
    padding: '0 !important',
    margin: '0 !important',
    justifyContent: 'flex-end',
  },
});

export function WriteReviewModal({
  ventureId,
  mentorName,
  mentorId,
  open,
  onClose,
}: WriteReviewModalProps) {
  const classes = useFormStyles();
  const { identityid } = useContext(UserContext);
  const { rb } = useResourceBundles();
  const { enqueueSnackbar } = useSnackbar();

  const [isLoadingVentures, setIsLoadingVentures] = useState(false);
  const [ventures, setVentures] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [showWarningModal, setShowWarningModal] = useState(false);

  const hasVentureId = Boolean(ventureId);

  useEffect(() => {
    if (open && !hasVentureId && identityid) {
      const loadVentures = async () => {
        try {
          setIsLoadingVentures(true);
          const loadedVentures =
            await venturesAPI.getVenturesByFounderAndMentor(
              identityid,
              mentorId,
            );

          // If no ventures returned (empty array means 404), show warning
          if (loadedVentures.length === 0) {
            setVentures([]);
            setShowWarningModal(true);
          } else {
            const ventureOptions = loadedVentures.map((venture) => ({
              value: venture.id,
              label: venture.ventureName,
            }));
            setVentures(ventureOptions);
          }
        } catch (error: any) {
          console.error('Error loading ventures:', error);
          enqueueSnackbar('Failed to load ventures', { variant: 'error' });
        } finally {
          setIsLoadingVentures(false);
        }
      };

      loadVentures();
    } else if (open && hasVentureId) {
      // If we have ventureId, we can show the form immediately
      setIsLoadingVentures(false);
    }
  }, [open, hasVentureId, identityid, mentorId, enqueueSnackbar, onClose]);

  const handleClose = () => {
    // Delay state reset to allow modal close animation to complete
    setTimeout(() => {
      setVentures([]);
      setShowWarningModal(false);
    }, 300); // Match modal animation duration
    onClose();
  };

  const handleWarningClose = () => {
    // Delay state reset to allow modal close animation to complete
    setTimeout(() => {
      setShowWarningModal(false);
    }, 300); // Match modal animation duration
    onClose();
  };

  const renderContent = () => {
    // If we have ventureId, show form immediately
    if (hasVentureId) {
      return (
        <WriteReviewForm
          onClose={handleClose}
          mentorName={mentorName}
          ventureId={ventureId}
          mentorId={mentorId}
        />
      );
    }

    // If no ventures found (404), show warning FIRST
    if (showWarningModal) {
      return (
        <>
          <Text variant='normal' className={classes.warningText}>
            You don't have any common ventures with this {rb('mentor')}. Reviews
            can only be written for {rb('mentor')}s you work with.
          </Text>
          <DialogActions className={classes.warningActions}>
            <Button
              variant='outlined'
              onClick={handleWarningClose}
              data-testid='button-ok'>
              Ok
            </Button>
          </DialogActions>
        </>
      );
    }

    // If loading ventures, show spinner
    if (isLoadingVentures) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            padding: '40px',
          }}>
          <CircularProgress size={40} />
        </div>
      );
    }

    // If ventures loaded successfully, show form with venture select
    return (
      <WriteReviewForm
        onClose={handleClose}
        mentorName={mentorName}
        mentorId={mentorId}
        ventures={ventures}
      />
    );
  };

  return (
    <>
      <ModalV2
        width={showWarningModal || isLoadingVentures ? '480px' : '720px'}
        open={open}
        onClose={handleClose}
        titleProps={{
          title: showWarningModal ? 'Cannot write a review' : 'Write a review',
        }}>
        {renderContent()}
      </ModalV2>
    </>
  );
}

function WriteReviewButton({
  ventureId,
  mentorName,
  mentorId,
}: WriteReviewProps) {
  const [open, setOpen] = useState(false);

  const handleOpen = (e: SyntheticEvent<any>) => {
    e.stopPropagation();
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <WriteReviewModal
        open={open}
        onClose={handleClose}
        ventureId={ventureId}
        mentorName={mentorName}
        mentorId={mentorId}
      />
      <Button
        onClick={handleOpen}
        className={CLASS_TRACKING.ENTITY_ACTION}
        data-testid='write-review-button'
        variant='outlined'
        color='primary'>
        Write a review
      </Button>
    </>
  );
}

function WriteReviewForm({
  onClose,
  ventureId,
  mentorId,
  mentorName = 'unknown',
  ventures = [],
}: WriteReviewFormProps) {
  const classes = useFormStyles();
  const { rb } = useResourceBundles();

  const [isLoading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const hasVentureId = Boolean(ventureId);

  const handleSubmit = async (formValues: FormValues) => {
    try {
      setLoading(true);
      const selectedVentureId = ventureId || formValues.ventureId;
      if (!selectedVentureId) {
        throw new Error('Venture ID is required');
      }
      await mentorsAPI.rateMentor(
        mentorId,
        selectedVentureId as VentureId,
        formValues,
      );
      setLoading(false);
      onClose();
      enqueueSnackbar(`${rb('mentor-u')} request sent successfully`, {
        variant: 'success',
      });
    } catch (error: any) {
      setLoading(false);
      const messageError = `Unfortunately, an error occurred when sending the ${rb(
        'mentor',
      )} request.\n Details: ${error?.response?.data?.message ||
      error?.message ||
      'Internal server error'
        }`;
      enqueueSnackbar(messageError, {
        variant: 'error',
        style: { whiteSpace: 'pre-line' },
      });
    }
  };

  return (
    <div>
      <Form
        validate={(values) => validateForm(values, hasVentureId)}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        render={(formProps) => (
          <form noValidate>
            <Typography className={classes.subtitle} variant='h5'>
              How would you rate {mentorName} as a {rb('mentor')} on your team?
            </Typography>
            {!hasVentureId && (
              <FormGroup>
                <Field<string>
                  name='ventureId'
                  component={FormSelect}
                  options={ventures}
                  label='Venture*'
                  testid='mentor-review-venture'
                />
              </FormGroup>
            )}
            <FormGroup>
              <Field<number>
                name='rating'
                component={FormRating}
                testid='mentor-review-rating'
              />
            </FormGroup>
            <FormGroup>
              <Field<string>
                name='comment'
                component={TextFieldWysiwyg}
                label='Comment'
                placeholder='Type here...'
                testid='mentor-review-comment'
                showCount
                maxLength={lengthField.wysiwygDisplayLimit}
              />
            </FormGroup>
            <div className={classes.contentButton}>
              <Button onClick={() => onClose()} variant='outlined'>
                Cancel
              </Button>
              <TestId testId='mentor-review-submit-button'>
                <Button
                  onClick={formProps.handleSubmit}
                  disabled={isLoading || !formProps.valid}>
                  {isLoading ? (
                    <CircularProgress size={24} color='inherit' />
                  ) : (
                    'Submit'
                  )}
                </Button>
              </TestId>
            </div>
          </form>
        )}
      />
    </div>
  );
}

export default WriteReviewButton;
